import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Navigation() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    // RTL support for Arabic
    if (lng === 'ar') {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img 
              src="https://static.readdy.ai/image/b9dffa7b6b8fd92109fabc077dd82307/a9b91868dc6fd723d43702c77f57a760.png" 
              alt="Mola Közde Kahveci Logo" 
              className="h-16 sm:h-20 w-auto object-contain"
            />
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-amber-700 font-medium transition-colors whitespace-nowrap cursor-pointer">
              {t('nav.about')}
            </button>
            <button onClick={() => scrollToSection('menu')} className="text-gray-700 hover:text-amber-700 font-medium transition-colors whitespace-nowrap cursor-pointer">
              {t('nav.menu')}
            </button>
            <button onClick={() => scrollToSection('gallery')} className="text-gray-700 hover:text-amber-700 font-medium transition-colors whitespace-nowrap cursor-pointer">
              {t('nav.gallery')}
            </button>
            <button onClick={() => scrollToSection('location')} className="text-gray-700 hover:text-amber-700 font-medium transition-colors whitespace-nowrap cursor-pointer">
              {t('nav.location')}
            </button>
            <button onClick={() => scrollToSection('contact')} className="px-6 py-2.5 bg-amber-700 text-white rounded-full hover:bg-amber-800 transition-colors font-medium whitespace-nowrap cursor-pointer">
              {t('nav.contact')}
            </button>
            
            <div className="flex items-center space-x-2 ml-4 border-l border-gray-300 pl-4">
              <button
                onClick={() => changeLanguage('tr')}
                className={`px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'tr' ? 'bg-amber-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                TR
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'en' ? 'bg-amber-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('ar')}
                className={`px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'ar' ? 'bg-amber-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                AR
              </button>
            </div>
          </div>

          <button 
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer">
              {t('nav.about')}
            </button>
            <button onClick={() => scrollToSection('menu')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer">
              {t('nav.menu')}
            </button>
            <button onClick={() => scrollToSection('gallery')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer">
              {t('nav.gallery')}
            </button>
            <button onClick={() => scrollToSection('location')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer">
              {t('nav.location')}
            </button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition-colors cursor-pointer">
              {t('nav.contact')}
            </button>
            
            <div className="flex items-center space-x-2 pt-3 border-t border-gray-200">
              <button
                onClick={() => changeLanguage('tr')}
                className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'tr' ? 'bg-amber-600 text-white' : 'text-gray-600 bg-gray-100'
                }`}
              >
                Türkçe
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'en' ? 'bg-amber-600 text-white' : 'text-gray-600 bg-gray-100'
                }`}
              >
                English
              </button>
              <button
                onClick={() => changeLanguage('ar')}
                className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'ar' ? 'bg-amber-600 text-white' : 'text-gray-600 bg-gray-100'
                }`}
              >
                العربية
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
